import React from 'react';

import { useUIColors } from '../../theme';
import { cx, defineRecipe, useRecipe, type Extensible } from '../../variants';

/**
 * `Extensible` because an app may add a tone through `UIVariantsProvider`
 * without this union learning about it — the known four still autocomplete.
 */
export type CalloutTone = Extensible<'warning' | 'danger' | 'info' | 'neutral'>;

/**
 * Two axes: the tone, and whether the lead-in is long enough to force the text
 * -block layout. Declared here so an app can add a fifth tone, or reskin one,
 * through the override registry instead of forking the component.
 */
export const calloutRecipe = defineRecipe('callout', 'ui-callout', {
    variants: {
        tone: {
            warning: 'ui-callout--warning',
            danger: 'ui-callout--danger',
            info: 'ui-callout--info',
            neutral: 'ui-callout--neutral',
        },
        layout: { row: 'ui-callout--row', block: 'ui-callout--block' },
    },
    defaultVariants: { tone: 'neutral', layout: 'row' },
});

export interface CalloutProps {
    children: React.ReactNode;
    tone?: CalloutTone;
    /**
     * Bold lead-in before the message, e.g. "Chưa có ánh xạ".
     *
     * Pass `longLabel` when this runs past roughly 40% of the callout's width
     * — see the component doc for why the layout has to change at that point.
     */
    label?: React.ReactNode;
    /**
     * Render the lead-in inline inside the text block instead of as a
     * fixed-width flex child. Required for long labels.
     */
    longLabel?: boolean;
    icon?: React.ReactNode;
}

/**
 * EAA-1804: the sheet's inline callout — tinted fill, 1px accent border with a
 * 4px leading edge.
 *
 * TWO LAYOUTS, AND THE CHOICE IS NOT COSMETIC. A short lead-in renders as a
 * `flex` row with `flex: none; white-space: nowrap` on the label — without
 * those it collapses to a ~60px column and wraps mid-phrase. A *long* lead-in
 * must not be a flex row at all: `nowrap` on a long label reserves half the
 * box, squeezing the sentence into a ragged column that breaks every three or
 * four words. Past roughly 40% of the callout width the sentence column falls
 * under ~55% of the box and the text block layout is the only readable one —
 * the handoff measured a 214px label in a 440px callout (48.6%) as clearly
 * past the line.
 *
 * Callers pick via `longLabel` rather than this component measuring itself:
 * measuring would need a layout effect and a re-render, and the caller
 * already knows whether its label is a two-word tag or half a sentence.
 *
 * Ant Design's `Alert` is not used here because its `message`/`description`
 * split hardcodes the stacked layout — the inline lead-in that this component
 * exists to get right is not expressible through it.
 *
 * A `danger` callout should state the consequence, not just the condition
 * ("nothing is granted", not merely "mapping paused").
 */
export function Callout({
    children,
    tone = 'neutral',
    label,
    longLabel = false,
    icon,
}: CalloutProps): React.ReactElement {
    const colors = useUIColors();
    const isBlock = Boolean(label && longLabel);
    const className = useRecipe(calloutRecipe, { tone, layout: isBlock ? 'block' : 'row' });

    const palettes: Record<string, { bg: string; border: string; fg: string }> = {
        warning: { bg: colors.warningBg, border: colors.warning, fg: colors.warningText },
        danger: { bg: colors.errorBg, border: colors.error, fg: colors.errorText },
        info: { bg: colors.infoBg, border: colors.infoBorder, fg: colors.info },
        // Neutral carries no accent edge — it is a note, not an alert.
        neutral: { bg: colors.fill, border: 'transparent', fg: colors.textSecondary },
    };
    // An app-defined tone has no inline palette here by design: it supplies its
    // own look through the class, and falling back to `neutral` keeps the box
    // legible rather than rendering it unstyled.
    const palette = palettes[tone] ?? palettes.neutral;
    const accented = tone !== 'neutral' && palettes[tone] !== undefined;

    const base: React.CSSProperties = {
        padding: '12px 14px',
        borderRadius: 8,
        background: palette.bg,
        border: accented ? `1px solid ${palette.border}` : 'none',
        borderLeftWidth: accented ? 4 : undefined,
        color: palette.fg,
        fontSize: accented ? 14 : 13,
        lineHeight: 1.55,
    };

    // Long lead-in: a normal text block, so the sentence continues on the same
    // line as the label and wraps at the full content width.
    if (isBlock) {
        return (
            <div
                role={tone === 'danger' ? 'alert' : undefined}
                className={className}
                style={base}
            >
                {icon && <span style={{ marginRight: 8 }}>{icon}</span>}
                <strong style={{ fontWeight: 700 }}>{label}</strong> <span>{children}</span>
            </div>
        );
    }

    // Short lead-in (or none): flex row. `flex: none` + `nowrap` on the label
    // are load-bearing — see the doc comment.
    return (
        <div
            role={tone === 'danger' ? 'alert' : undefined}
            className={cx(className)}
            style={{ ...base, display: 'flex', gap: 10, alignItems: 'flex-start' }}
        >
            {icon && <span style={{ flex: 'none' }}>{icon}</span>}
            {label && (
                <strong style={{ flex: 'none', whiteSpace: 'nowrap', fontWeight: 700 }}>
                    {label}
                </strong>
            )}
            <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
        </div>
    );
}

export default Callout;
